import React, { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { Arrow90degDown, ArrowClockwise, ChevronBarContract, ChevronBarExpand, FiletypeCsv, GraphDownArrow, Search } from "react-bootstrap-icons";
import { useNavigate, useParams } from "react-router";
import { BindableFormCheck } from "../components/BindableFormCheck";
import { GroupingDisplay } from "../components/GroupingDisplay";
import { LoadingComponent } from "../components/LoadingComponent";
import { useProfile, useWindowSettings } from "../components/ProfileContext";
import { SimpleFormField } from "../components/SimpleFormField";
import { SpinnerButton } from "../components/SpinnerButton";
import { Inspection } from "../models/Inspection";
import { InspectionPart } from "../models/InspectionPart";
import { afterRenderDelay, arrayToCSV, downloadBlobToNewWindow, formatFilename, inspectionSearch, navigateToInspectionId, openLinkExternal, setNavAdvisoryLockout, useIsVault } from "../util/utils";

export function PartsListingPage(props) {
	setNavAdvisoryLockout(false);

	let [showCsvDownloadButton, setShowCsvDownloadButton] = useState(true);
	let [profile] = useProfile();
	let [parts, setParts] = useState(null);
	let isVault = useIsVault();
	let [showSearchTools, setShowSearchTools] = useState(isVault);
	let dataGroupingsRef = useRef();

	let job = profile.job;

	let partRowBuilder = (grp, part) => {
		return <JobPartRow key={part.id} profile={profile} part={part} />
	};

	let partHeaderBuilder = (grp) => {
		return <tr>
			<th></th>
			<th>Asset</th>
			<th>Model</th>
			<th>Part</th>
			<th>Part&nbsp;#</th>
			<th>Replaced</th>
			<th>Ordered</th>
		</tr>;
	};

	useEffect(async () => {		
		if(parts == null) {
			let allParts = {};

			// Search param setup
			let searchParams = {};
			if(job != null) {
				searchParams.jobId = job.id;
			}

			// Load remote parts
			let tmpParts = await InspectionPart.getParts(profile, searchParams);
			tmpParts.forEach(p => allParts[p.id] = p);

			// Load local parts (favoring local over remote parts)
			let localInspections = await Inspection.loadAllFromLocalByJobId(job?.id);
			localInspections.forEach(insp => {
				insp.inspectionParts.forEach(p => {
					p.inspection = insp; // Cache
					allParts[p.id] = p;
				});
			});

			setParts(Object.values(allParts));
		}
	});

	let generateCSVDataAction = () => {
		let data = [["Asset", "Model", "Part", "Part #", "Replaced", "Ordered"]];
		dataGroupingsRef.current.forEach(grp => {
			grp.data.forEach(part => {
				let row = [
					part.inspection.componentAssetNumber,
					part.inspection.componentModelNumber,
					part.name,
					part.partNumber,
					part.replaced ? "Y" : "N",
					part.ordered ? "Y" : "N"
				];
				data.push(row);
			});
		});
		
		let csvData = arrayToCSV(data);
		let csvBlob = new Blob([csvData], {type:"text/csv"});
		downloadBlobToNewWindow(csvBlob, formatFilename("csv", profile.dateFormatter(new Date()), profile.job?.name, "parts"));
	};

	let groupingOptions = [
		{
			name: "asset",
			keyPath: ["inspection", "componentAssetNumber"],
			label: "Asset #"
		},
		{
			name: "model",
			keyPath: ["inspection", "componentModelNumber"],
			label: "Model #"
		},
		{
			name: "company",
			keyPath: ["inspection", "companyName"],
			label: "Company"
		},
		{
			name: "location",
			keyPath: ["inspection", "location"],
			label: "Location"
		},
		{
			name: "ordered",
			keyPath: ["ordered"],
			label: "Ordered"
		},
		{
			name: "replaced",
			keyPath: ["replaced"],
			label: "Replaced"
		}
	];

	let sortingOptions = [
		{
			name: "asset",
			keyPath: ["inspection", "componentAssetNumber"],
			label: "Asset #"
		},
		{
			name: "part",
			keyPath: ["partNumber"],
			label: "Part #"
		}
	];

	let filterKeyPaths = [
		["name"],
		["partNumber"],
		["notes"],
		["inspection", "componentAssetNumber"],
		["inspection", "componentModelNumber"],
		["inspection", "componentSerialNumber"]
	];

	let afterProcessingGroups = (count) => {
		setShowCsvDownloadButton(count > 0);
	};

	return <>
		<div className="headerOnlyContainer">
			<h1>
				Parts
				{showCsvDownloadButton && <Button onClick={generateCSVDataAction} size="sm" className="float-end mt-0 ms-4 filetypeButton"><FiletypeCsv className="filetypeIcon" /></Button>}
				<Button size="sm" variant="info" className="float-end mt-1 ms-4" onClick={() => {setShowSearchTools(!showSearchTools)}}><Search /> Tools</Button>
			</h1>
			<div className="clearfix"></div>
			{parts == null && <LoadingComponent />}
		</div>
		{parts != null && <GroupingDisplay toolsClassName="pageSidePadding" dataGroupingsRef={dataGroupingsRef} processingCallback={afterProcessingGroups} groupingOptions={groupingOptions} sortingOptions={sortingOptions} data={parts} headerBuilder={partHeaderBuilder} rowBuilder={partRowBuilder} filterKeyPaths={filterKeyPaths} showSearchTools={showSearchTools} />}
	</>;
}

function JobPartRow(props) {
	let { profile, part } = props;
	let [collapsed, setCollapsed] = useState(true);
	let [saving, setSaving] = useState(false);
	let navigate = useNavigate();

	let insp = part.inspection;

	let saveAction = () => {
		setSaving(true);
		afterRenderDelay(async () => {
			try {
				await part.sendToServer(profile);
				setCollapsed(true);
			} catch(err) {
				alert(`Unable to save part: ${err}`);
			}
			setSaving(false);
	
		});
	};

	let inspectionAction = () => {
		try {
			navigateToInspectionId(profile, navigate, insp.id);
		} catch(err) {
			alert(`Unable to view inspection: ${err}`);
		}
	};

	let showPartsFinderAction = async () => {
		openLinkExternal(await part.buildPartsFinderUrl());
	};

	return <React.Fragment>
		<tr>
			<td>
				{collapsed && <span onClick={() => {setCollapsed(!collapsed)}}>{collapsed ? <ChevronBarExpand /> : <ChevronBarContract />}</span>}
			</td>
			<td>{insp.componentAssetNumber}</td>
			<td>{insp.componentModelNumber}</td>
			<td>{part.name}</td>
			<td>{part.partNumber}</td>
			<td>{part.replaced ? "Y" : "N"}</td>
			<td>{part.ordered ? "Y" : "N"}</td>
		</tr>
		{ !collapsed && <tr className="table-active">
			<td></td>
			<td colSpan={6}>
				<Button className="my-2" onClick={inspectionAction} variant="warning">Go to Inspection</Button>
				<SpinnerButton className="m-2 float-end" onClick={saveAction} spinning={saving}>Save</SpinnerButton>
				<Button className="m-2 float-end" variant="secondary" onClick={() => setCollapsed(!collapsed)}>Cancel</Button>
				<Button className="m-2 float-end" variant="info" onClick={showPartsFinderAction}>Part Finder <Search /></Button>
				<div className="clearfix"></div>
				<SimpleFormField className="my-2" label="Part Name" bindObject={part} bindAttribute="name" />
				<SimpleFormField className="my-2" label="Part Number" bindObject={part} bindAttribute="partNumber" />
				<SimpleFormField className="my-2" label="Notes" bindObject={part} bindAttribute="notes" />
				<BindableFormCheck bindObject={part} bindAttribute="replaced" label="Replaced" className="mt-4" />
				<BindableFormCheck bindObject={part} bindAttribute="ordered" label="Ordered" />
			</td>
		</tr>}
	</React.Fragment>;
}